// device-control.js
// 设备控制通用模块

/**
 * 设备控制类
 * 支持单个设备和批量设备的控制操作
 */
class DeviceControl {
    constructor(axios, message, confirm) {
        this.$axios = axios
        this.$message = message
        this.$confirm = confirm
    }

    /**
     * 获取控制类型显示文本（仅用于UI显示）
     */
    getControlTypeText(controlType) {
        const textMap = {
            'SWITCH_ON': '合闸',
            'SWITCH_OFF': '分闸',
            'SELF_CHECK': '漏电自检',
            'CLEAR_FAULT': '清除故障',
            'UNLOCK': '解除锁定'
        }
        return textMap[controlType] || controlType
    }

    /**
     * 执行单个设备控制指令
     * @param {Object} device 设备对象 { deviceSn, deviceName, deviceNodeId }
     * @param {string} controlType 控制类型（直接透传给后端）
     * @param {Object} customParams 自定义参数（品牌特定参数）
     * @param {Function} onSuccess 成功回调
     * @param {Function} onError 失败回调
     */
    async execute(device, controlType, customParams = {}, onSuccess, onError) {
        if (!device) {
            this.$message?.warning('请先选择设备')
            return false
        }

        const txt = this.getControlTypeText(controlType)
        const deviceName = device.deviceName || device.name || '未知设备'

        try {
            await this.$confirm?.(`确定要对设备【${deviceName}】执行【${txt}】操作吗？`, '提示', {
                type: 'warning',
                confirmButtonText: '确定',
                cancelButtonText: '取消'
            })
        } catch {
            return false
        }

        try {
            const requestParams = {
                deviceSn: device.deviceSn,
                params: {
                    functionCode: controlType,
                    controlType: controlType,
                    ...customParams,
                    address: [device.deviceNodeId || 0],
                    childDeviceId: [device.deviceSn]
                }
            }

            if (customParams.params) {
                requestParams.params = customParams.params
            }

            const response = await this.$axios.post('/api/command/executeCONTROL', requestParams)

            if (response.data.code === 0) {
                this.$message?.success(`设备【${deviceName}】${txt}指令发送成功`)
                onSuccess?.(response.data, device)
                return true
            } else {
                this.$message?.error(response.data.msg || `设备【${deviceName}】${txt}失败`)
                onError?.(response.data, device)
                return false
            }
        } catch (error) {
            
            this.$message?.error(`设备【${deviceName}】${txt}失败：${error.response?.data?.msg || error.message || '未知错误'}`)
            onError?.(error, device)
            return false
        }
    }

    /**
     * 批量执行控制指令
     * @param {Array} devices 设备列表 [{ deviceSn, deviceName, deviceNodeId }]
     * @param {string} controlType 控制类型（直接透传给后端）
     * @param {Object} customParams 自定义参数（品牌特定参数）
     * @param {Object} options 选项 { stopOnError: false, maxBatchSize: 50, interval: 5000, showResultDialog: true }
     * @param {Function} onProgress 进度回调
     * @param {Function} onComplete 完成回调
     */
    async batchExecute(devices, controlType, customParams = {}, options = {}, onProgress, onComplete) {
        const maxBatchSize = options.maxBatchSize || 50
        const minInterval = options.interval || 5000
        const showResultDialog = options.showResultDialog !== false

        if (!devices || devices.length === 0) {
            this.$message?.warning('请选择要操作的设备')
            return { success: false, results: [] }
        }

        if (devices.length > maxBatchSize) {
            this.$message?.warning(`最多支持批量操作${maxBatchSize}台设备，当前选择了${devices.length}台`)
            return { success: false, results: [] }
        }

        const txt = this.getControlTypeText(controlType)

        try {
            await this.$confirm?.(`确定要对 ${devices.length} 个设备执行批量【${txt}】操作吗？`, '提示', {
                type: 'warning',
                confirmButtonText: '确定',
                cancelButtonText: '取消'
            })
        } catch {
            return { success: false, cancelled: true }
        }

        const addresses = devices.map(d => d.deviceNodeId || d.nodeId || 0)
        const childDeviceIds = devices.map(d => d.deviceSn)

        onProgress?.({
            currentBatch: 1,
            totalBatches: 1,
            processedCount: 0,
            totalCount: devices.length,
            status: 'executing'
        })

        try {
            const requestParams = {
                devices: devices.map(d => d.deviceSn),
                params: {
                    functionCode: controlType,
                    controlType: controlType,
                    address: addresses,
                    childDeviceId: childDeviceIds,
                    ...customParams
                }
            }

            if (customParams.params) {
                requestParams.params = customParams.params
            }

            const response = await this.$axios.post('/api/command/executeCONTROL/batch', requestParams)

            onProgress?.({
                processedCount: devices.length,
                totalCount: devices.length,
                status: 'completed'
            })

            if (response.data.code === 0) {
                const result = response.data.data || {}
                this.$message?.success(`批量${txt}指令发送成功，共 ${devices.length} 台设备`)

                // 显示详细结果
                if (showResultDialog && result.results && result.results.length > 0) {
                    const successCount = result.results.filter(r => r.success).length
                    const failCount = result.results.filter(r => !r.success).length

                    if (failCount > 0) {
                        this.$confirm?.(
                            `<p>批量执行完成！</p>
               <p>成功: ${successCount} 个</p>
               <p>失败: ${failCount} 个</p>
               <p style="color:#F56C6C">失败设备: ${result.results.filter(r => !r.success).map(r => r.deviceName || r.deviceSn).join(', ')}</p>`,
                            '批量执行结果',
                            {
                                dangerouslyUseHTMLString: true,
                                type: 'warning',
                                showCancelButton: false,
                                confirmButtonText: '确定'
                            }
                        ).catch(() => {})
                    } else {
                        this.$message?.success(`批量${txt}成功，共 ${devices.length} 台设备`)
                    }
                }

                onComplete?.(result, devices)
                return { success: true, data: result }
            } else {
                this.$message?.error(response.data.msg || `批量${txt}失败`)
                onComplete?.({ error: response.data.msg }, devices)
                return { success: false, error: response.data.msg }
            }
        } catch (error) {
            
            this.$message?.error(`批量${txt}失败：${error.response?.data?.msg || error.message || '未知错误'}`)
            onComplete?.(error, devices)
            return { success: false, error }
        }
    }

    /**
     * 带间隔的批量执行（自动处理间隔）
     * @param {Array} devices 设备列表
     * @param {string} controlType 控制类型
     * @param {Object} customParams 自定义参数
     * @param {Object} options 选项 { batchSize: 50, interval: 5000 }
     * @param {Function} onProgress 进度回调
     */
    async batchExecuteWithInterval(devices, controlType, customParams = {}, options = {}, onProgress) {
        const batchSize = options.batchSize || 50
        const interval = options.interval || 5000

        const results = {
            totalCount: devices.length,
            successCount: 0,
            failedCount: 0,
            details: []
        }

        const totalBatches = Math.ceil(devices.length / batchSize)

        for (let i = 0; i < devices.length; i += batchSize) {
            const batch = devices.slice(i, i + batchSize)
            const batchIndex = Math.floor(i / batchSize) + 1

            onProgress?.({
                currentBatch: batchIndex,
                totalBatches: totalBatches,
                processedCount: i,
                totalCount: devices.length,
                status: 'executing'
            })

            const batchResult = await this.batchExecute(
                batch,
                controlType,
                customParams,
                { ...options, showResultDialog: false },
                null,
                null
            )

            if (batchResult.success && batchResult.data?.results) {
                for (const detail of batchResult.data.results) {
                    results.details.push(detail)
                    if (detail.success) {
                        results.successCount++
                    } else {
                        results.failedCount++
                    }
                }
            } else {
                for (const device of batch) {
                    results.details.push({
                        deviceSn: device.deviceSn,
                        deviceName: device.deviceName,
                        success: false,
                        message: batchResult.error || '执行失败'
                    })
                    results.failedCount++
                }
            }

            // 不是最后一批，等待间隔时间
            if (i + batchSize < devices.length) {
                await new Promise(resolve => setTimeout(resolve, interval))
            }
        }

        onProgress?.({
            processedCount: devices.length,
            totalCount: devices.length,
            status: 'completed',
            results: results
        })

        const txt = this.getControlTypeText(controlType)
        if (results.failedCount === 0) {
            this.$message?.success(`批量${txt}完成，共 ${results.successCount} 台设备`)
        } else {
            this.$message?.warning(`批量${txt}完成，成功 ${results.successCount} 台，失败 ${results.failedCount} 台`)
        }

        return results
    }
}

/**
 * Vue 插件形式导出
 */
export default {
    install(Vue, options) {
        const deviceControl = new DeviceControl(
            Vue.prototype.$axios,
            Vue.prototype.$message,
            Vue.prototype.$confirm
        )

        Vue.prototype.$deviceControl = deviceControl
        Vue.deviceControl = deviceControl
    }
}

export { DeviceControl }